import { Material } from "@animech/axiom-format/src/definitions/definition-bindings";
import { SeatingType, TableType } from "src/definitions/type-definitions";

export type ProductType = TableType | SeatingType;

/**
 * Builds a canonical lookup key for the SKU table.
 * Format: "ProductType|Wood|Metal" or "ProductType|Metal" (when no wood).
 */
export function skuKey(
	productType: ProductType,
	wood: Material | undefined,
	metal: Material,
): string {
	if (wood === undefined) {
		return `${productType}|${metal}`;
	}
	return `${productType}|${wood}|${metal}`;
}

/* eslint-disable @animech/axiom/no-references-in-top-level-values */

/**
 * Flat lookup table mapping skuKey() output → API SKU string.
 * Built by cross-referencing valid combos from tableOptions/chairOptions
 * with products-in-api.json.
 *
 * Pine + Galvanized combos are excluded from material options (no API
 * product exists). Pine is only valid with Green metal base.
 * Bench-8 no longer offers Pine wood (only metal is Galv → no valid combo).
 */
export const skuTable: Record<string, string> = {
	// ── Chair-A2 (Fåtölj A2) ──────────────────────────────────────────
	[skuKey("Chair-A2", "WoodPineOiled", "PaintMetalGreen")]: "10105",
	[skuKey("Chair-A2", "WoodOakOiled", "MetalGalvanizedSteel")]: "10185",
	[skuKey("Chair-A2", "PaintWoodRed", "MetalGalvanizedSteel")]: "1012511",
	[skuKey("Chair-A2", "PaintWoodGreen", "MetalGalvanizedSteel")]: "1012551",
	[skuKey("Chair-A2", "LacquerWhite", "MetalGalvanizedSteel")]: "10141",
	[skuKey("Chair-A2", "WoodOakOiled", "PaintMetalBlack")]: "1012585",
	[skuKey("Chair-A2", "LacquerWhite", "PaintMetalBlack")]: "1012541",
	[skuKey("Chair-A2", "PaintWoodGreen", "PaintMetalBlack")]: "1012561",
	[skuKey("Chair-A2", "PaintWoodRed", "PaintMetalBlack")]: "1012611",
	[skuKey("Chair-A2", "WoodTeakUntreated", "MetalGalvanizedSteel")]: "10131",
	[skuKey("Chair-A2", "WoodTeakUntreated", "PaintMetalBlack")]: "1012531",

	// ── Chair-1 (Stol 1) ──────────────────────────────────────────────
	[skuKey("Chair-1", "WoodPineOiled", "PaintMetalGreen")]: "101051",
	[skuKey("Chair-1", "WoodOakOiled", "MetalGalvanizedSteel")]: "101851",
	[skuKey("Chair-1", "PaintWoodRed", "MetalGalvanizedSteel")]: "10125112",
	[skuKey("Chair-1", "PaintWoodGreen", "MetalGalvanizedSteel")]: "10125512",
	[skuKey("Chair-1", "LacquerWhite", "MetalGalvanizedSteel")]: "101412",
	[skuKey("Chair-1", "WoodOakOiled", "PaintMetalBlack")]: "10125851",
	[skuKey("Chair-1", "LacquerWhite", "PaintMetalBlack")]: "10125412",
	[skuKey("Chair-1", "PaintWoodGreen", "PaintMetalBlack")]: "10125612",
	[skuKey("Chair-1", "PaintWoodRed", "PaintMetalBlack")]: "10126112",
	[skuKey("Chair-1", "WoodTeakUntreated", "MetalGalvanizedSteel")]: "101312",
	[skuKey("Chair-1", "WoodTeakUntreated", "PaintMetalBlack")]: "10125312",

	// ── Chair-HighTech (Stol High Tech) ───────────────────────────────
	[skuKey("Chair-HighTech", undefined, "MetalGalvanizedSteel")]: "10601",
	[skuKey("Chair-HighTech", undefined, "PaintMetalPowdercoatChestnut")]:
		"1062502",
	[skuKey("Chair-HighTech", undefined, "PaintMetalPowdercoatIvory")]: "1062504",
	[skuKey("Chair-HighTech", undefined, "PaintMetalPowdercoatPetrol")]:
		"1062505",
	[skuKey("Chair-HighTech", undefined, "PaintMetalPowdercoatBlack")]: "1062507",

	// ── Chair-Bovik (Fåtölj Bovik) ────────────────────────────────────
	[skuKey("Chair-Bovik", "WoodTeakUntreated", "MetalGalvanizedSteel")]: "10810",
	[skuKey("Chair-Bovik", "WoodTeakUntreated", "PaintMetalBlack")]: "10820",

	// ── Chair-Libelle (Stol Libelle) ──────────────────────────────────
	[skuKey("Chair-Libelle", undefined, "PaintMetalGrey")]: "107204",
	[skuKey("Chair-Libelle", undefined, "PaintMetalGreen")]: "107202",
	[skuKey("Chair-Libelle", undefined, "PaintMetalGraphite")]: "107203",

	// ── Bench-8 (Bänk 8) ─────────────────────────────────────────────
	[skuKey("Bench-8", "WoodOakOiled", "MetalGalvanizedSteel")]: "10358",
	[skuKey("Bench-8", "LacquerWhite", "MetalGalvanizedSteel")]: "10354",
	[skuKey("Bench-8", "WoodTeakUntreated", "MetalGalvanizedSteel")]: "10353",

	// ── Bench-9 (Bänk 9) ─────────────────────────────────────────────
	[skuKey("Bench-9", "WoodOakOiled", "MetalGalvanizedSteel")]: "10368",
	[skuKey("Bench-9", "LacquerWhite", "MetalGalvanizedSteel")]: "10364",
	[skuKey("Bench-9", "WoodTeakUntreated", "MetalGalvanizedSteel")]: "10363",

	// ── Bench-Libelle (Bänk Libelle) ──────────────────────────────────
	[skuKey("Bench-Libelle", undefined, "PaintMetalGreen")]: "10726232",
	[skuKey("Bench-Libelle", undefined, "PaintMetalGraphite")]: "10726233",
	[skuKey("Bench-Libelle", undefined, "PaintMetalGrey")]: "10726234",

	// ── Sofa-3 (Soffa 3) ─────────────────────────────────────────────
	[skuKey("Sofa-3", "WoodOakOiled", "MetalGalvanizedSteel")]: "1032678",
	[skuKey("Sofa-3", "LacquerWhite", "MetalGalvanizedSteel")]: "1032674",
	[skuKey("Sofa-3", "WoodOakOiled", "PaintMetalBlack")]: "10326781",
	[skuKey("Sofa-3", "WoodTeakUntreated", "MetalGalvanizedSteel")]: "1032673",
	[skuKey("Sofa-3", "WoodTeakUntreated", "PaintMetalBlack")]: "10326731",

	// ── Sofa-5 (Soffa 5) ─────────────────────────────────────────────
	[skuKey("Sofa-5", "WoodOakOiled", "MetalGalvanizedSteel")]: "10317",
	[skuKey("Sofa-5", "LacquerWhite", "MetalGalvanizedSteel")]: "10315",
	[skuKey("Sofa-5", "WoodOakOiled", "PaintMetalBlack")]: "1032617",
	[skuKey("Sofa-5", "WoodTeakUntreated", "MetalGalvanizedSteel")]: "10312",
	[skuKey("Sofa-5", "WoodTeakUntreated", "PaintMetalBlack")]: "1032612",

	// ── Table-B31-84 (Bord B31 84) ────────────────────────────────────
	[skuKey("Table-B31-84", "WoodOakOiled", "MetalGalvanizedSteel")]: "101857",
	[skuKey("Table-B31-84", "PaintWoodRed", "MetalGalvanizedSteel")]: "1012517",
	[skuKey("Table-B31-84", "PaintWoodGreen", "MetalGalvanizedSteel")]: "1012557",
	[skuKey("Table-B31-84", "LacquerWhite", "MetalGalvanizedSteel")]: "10147",
	[skuKey("Table-B31-84", "WoodOakOiled", "PaintMetalBlack")]: "10125857",
	[skuKey("Table-B31-84", "LacquerWhite", "PaintMetalBlack")]: "1012547",
	[skuKey("Table-B31-84", "PaintWoodGreen", "PaintMetalBlack")]: "1012567",
	[skuKey("Table-B31-84", "PaintWoodRed", "PaintMetalBlack")]: "1012617",
	[skuKey("Table-B31-84", "WoodTeakUntreated", "MetalGalvanizedSteel")]:
		"10137",
	[skuKey("Table-B31-84", "WoodTeakUntreated", "PaintMetalBlack")]: "1012537",

	// ── Table-B31-170 (Bord B31 170) ──────────────────────────────────
	[skuKey("Table-B31-170", "WoodOakOiled", "MetalGalvanizedSteel")]: "101856",
	[skuKey("Table-B31-170", "PaintWoodRed", "MetalGalvanizedSteel")]: "1012516",
	[skuKey("Table-B31-170", "PaintWoodGreen", "MetalGalvanizedSteel")]:
		"1012556",
	[skuKey("Table-B31-170", "LacquerWhite", "MetalGalvanizedSteel")]: "10146",
	[skuKey("Table-B31-170", "WoodOakOiled", "PaintMetalBlack")]: "10125856",
	[skuKey("Table-B31-170", "LacquerWhite", "PaintMetalBlack")]: "1012546",
	[skuKey("Table-B31-170", "PaintWoodGreen", "PaintMetalBlack")]: "1012566",
	[skuKey("Table-B31-170", "PaintWoodRed", "PaintMetalBlack")]: "1012616",
	[skuKey("Table-B31-170", "WoodTeakUntreated", "MetalGalvanizedSteel")]:
		"10136",
	[skuKey("Table-B31-170", "WoodTeakUntreated", "PaintMetalBlack")]: "1012536",

	// ── Table-B31-230 (Bord B31 230) ──────────────────────────────────
	[skuKey("Table-B31-230", "WoodOakOiled", "MetalGalvanizedSteel")]: "1018581",
	[skuKey("Table-B31-230", "PaintWoodRed", "MetalGalvanizedSteel")]: "1012518",
	[skuKey("Table-B31-230", "PaintWoodGreen", "MetalGalvanizedSteel")]:
		"10125581",
	[skuKey("Table-B31-230", "LacquerWhite", "MetalGalvanizedSteel")]: "101481",
	[skuKey("Table-B31-230", "WoodOakOiled", "PaintMetalBlack")]: "101258581",
	[skuKey("Table-B31-230", "LacquerWhite", "PaintMetalBlack")]: "10125481",
	[skuKey("Table-B31-230", "PaintWoodGreen", "PaintMetalBlack")]: "10125681",
	[skuKey("Table-B31-230", "PaintWoodRed", "PaintMetalBlack")]: "1012618",
	[skuKey("Table-B31-230", "WoodTeakUntreated", "MetalGalvanizedSteel")]:
		"101381",
	[skuKey("Table-B31-230", "WoodTeakUntreated", "PaintMetalBlack")]: "10125381",

	// ── Table-6B-60 (Bord 6B 60) ──────────────────────────────────────
	[skuKey("Table-6B-60", "WoodPineOiled", "PaintMetalGreen")]: "101260526",
	[skuKey("Table-6B-60", "WoodOakOiled", "MetalGalvanizedSteel")]: "1018526",
	[skuKey("Table-6B-60", "PaintWoodRed", "MetalGalvanizedSteel")]: "10125126",
	[skuKey("Table-6B-60", "PaintWoodGreen", "MetalGalvanizedSteel")]: "10125526",
	[skuKey("Table-6B-60", "LacquerWhite", "MetalGalvanizedSteel")]: "101426",
	[skuKey("Table-6B-60", "WoodOakOiled", "PaintMetalBlack")]: "101258526",
	[skuKey("Table-6B-60", "LacquerWhite", "PaintMetalBlack")]: "10125426",
	[skuKey("Table-6B-60", "PaintWoodGreen", "PaintMetalBlack")]: "10125626",
	[skuKey("Table-6B-60", "PaintWoodRed", "PaintMetalBlack")]: "10126126",
	[skuKey("Table-6B-60", "WoodTeakUntreated", "MetalGalvanizedSteel")]:
		"101326",
	[skuKey("Table-6B-60", "WoodTeakUntreated", "PaintMetalBlack")]: "10125326",

	// ── Table-9A-100 (Bord 9A 100) ────────────────────────────────────
	[skuKey("Table-9A-100", "WoodPineOiled", "PaintMetalGreen")]: "101052",
	[skuKey("Table-9A-100", "WoodOakOiled", "MetalGalvanizedSteel")]: "101852",
	[skuKey("Table-9A-100", "PaintWoodRed", "MetalGalvanizedSteel")]: "1012512",
	[skuKey("Table-9A-100", "PaintWoodGreen", "MetalGalvanizedSteel")]: "1012552",
	[skuKey("Table-9A-100", "LacquerWhite", "MetalGalvanizedSteel")]: "101421",
	[skuKey("Table-9A-100", "LacquerWhite", "PaintMetalBlack")]: "1012542",
	[skuKey("Table-9A-100", "PaintWoodGreen", "PaintMetalBlack")]: "1012562",
	[skuKey("Table-9A-100", "WoodOakOiled", "PaintMetalBlack")]: "10125852",
	[skuKey("Table-9A-100", "PaintWoodRed", "PaintMetalBlack")]: "1012612",
	[skuKey("Table-9A-100", "WoodTeakUntreated", "MetalGalvanizedSteel")]:
		"101321",
	[skuKey("Table-9A-100", "WoodTeakUntreated", "PaintMetalBlack")]: "1012532",

	// ── Table-9A-120 (Bord 9A 120) ────────────────────────────────────
	[skuKey("Table-9A-120", "WoodPineOiled", "PaintMetalGreen")]: "101053",
	[skuKey("Table-9A-120", "WoodOakOiled", "MetalGalvanizedSteel")]: "101853",
	[skuKey("Table-9A-120", "PaintWoodRed", "MetalGalvanizedSteel")]: "1012513",
	[skuKey("Table-9A-120", "PaintWoodGreen", "MetalGalvanizedSteel")]: "1012553",
	[skuKey("Table-9A-120", "LacquerWhite", "MetalGalvanizedSteel")]: "101431",
	[skuKey("Table-9A-120", "WoodOakOiled", "PaintMetalBlack")]: "10125853",
	[skuKey("Table-9A-120", "LacquerWhite", "PaintMetalBlack")]: "1012543",
	[skuKey("Table-9A-120", "PaintWoodGreen", "PaintMetalBlack")]: "1012563",
	[skuKey("Table-9A-120", "PaintWoodRed", "PaintMetalBlack")]: "1012613",
	[skuKey("Table-9A-120", "WoodTeakUntreated", "MetalGalvanizedSteel")]:
		"101331",
	[skuKey("Table-9A-120", "WoodTeakUntreated", "PaintMetalBlack")]: "1012533",

	// ── Table-B25A-120 (Bord B25A 120) ────────────────────────────────
	[skuKey("Table-B25A-120", "WoodPineOiled", "PaintMetalGreen")]: "101054",
	[skuKey("Table-B25A-120", "WoodOakOiled", "MetalGalvanizedSteel")]: "101854",
	[skuKey("Table-B25A-120", "PaintWoodRed", "MetalGalvanizedSteel")]: "1012514",
	[skuKey("Table-B25A-120", "PaintWoodGreen", "MetalGalvanizedSteel")]:
		"1012554",
	[skuKey("Table-B25A-120", "LacquerWhite", "MetalGalvanizedSteel")]: "101441",
	[skuKey("Table-B25A-120", "WoodOakOiled", "PaintMetalBlack")]: "10125854",
	[skuKey("Table-B25A-120", "LacquerWhite", "PaintMetalBlack")]: "1012544",
	[skuKey("Table-B25A-120", "PaintWoodGreen", "PaintMetalBlack")]: "1012564",
	[skuKey("Table-B25A-120", "PaintWoodRed", "PaintMetalBlack")]: "1012614",
	[skuKey("Table-B25A-120", "WoodTeakUntreated", "MetalGalvanizedSteel")]:
		"101341",
	[skuKey("Table-B25A-120", "WoodTeakUntreated", "PaintMetalBlack")]: "1012534",

	// ── Table-HighTech-60 (Bord High Tech 60) ─────────────────────────
	[skuKey("Table-HighTech-60", "LaminateWhite", "MetalGalvanizedSteel")]:
		"10610",
	[skuKey("Table-HighTech-60", "LaminateGraphite", "MetalGalvanizedSteel")]:
		"1062610",
	[skuKey("Table-HighTech-60", "LaminateWhite", "PaintMetalBlack")]: "1062512",
	[skuKey("Table-HighTech-60", "LaminateGraphite", "PaintMetalBlack")]:
		"1062612",

	// ── Table-HighTech-110 (Bord High Tech 110) ───────────────────────
	[skuKey("Table-HighTech-110", "LaminateWhite", "MetalGalvanizedSteel")]:
		"10620",
	[skuKey("Table-HighTech-110", "LaminateGraphite", "MetalGalvanizedSteel")]:
		"1062620",
	[skuKey("Table-HighTech-110", "LaminateWhite", "PaintMetalBlack")]: "1062522",
	[skuKey("Table-HighTech-110", "LaminateGraphite", "PaintMetalBlack")]:
		"1062622",

	// ── Table-Libelle-140 (Bord Libelle 140) ──────────────────────────
	[skuKey("Table-Libelle-140", undefined, "PaintMetalGreen")]: "10726222",
	[skuKey("Table-Libelle-140", undefined, "PaintMetalGraphite")]: "10726223",
	[skuKey("Table-Libelle-140", undefined, "PaintMetalGrey")]: "10726224",

	// ── Table-Libelle-70 (Bord Libelle 70) ────────────────────────────
	[skuKey("Table-Libelle-70", undefined, "PaintMetalGreen")]: "107212",
	[skuKey("Table-Libelle-70", undefined, "PaintMetalGrey")]: "107214",
	[skuKey("Table-Libelle-70", undefined, "PaintMetalGraphite")]: "107213",

	// ── Table-Bovik-150 (Bord Bovik 150) ──────────────────────────────
	[skuKey("Table-Bovik-150", "WoodTeakUntreated", "MetalGalvanizedSteel")]:
		"10814",
	[skuKey("Table-Bovik-150", "WoodTeakUntreated", "PaintMetalBlack")]: "10824",
};

/* eslint-enable @animech/axiom/no-references-in-top-level-values */

// ---------------------------------------------------------------------------
// Inverse SKU lookup — used by the single-product viewer to resolve
// `/#view?article=<SKU>` into a configurator state. Built once at module load
// by reversing skuTable. See docs/adr/0001-viewer-url-contract-is-raw-sku.md.
// ---------------------------------------------------------------------------

export type SkuCombo = {
	productType: ProductType;
	wood: Material | undefined;
	metal: Material;
};

const comboFromSku: Map<string, SkuCombo> = (() => {
	const map = new Map<string, SkuCombo>();
	for (const [key, sku] of Object.entries(skuTable)) {
		const parts = key.split("|");
		if (parts.length === 2) {
			map.set(sku, {
				productType: parts[0] as ProductType,
				wood: undefined,
				metal: parts[1] as Material,
			});
		} else if (parts.length === 3) {
			map.set(sku, {
				productType: parts[0] as ProductType,
				wood: parts[1] as Material,
				metal: parts[2] as Material,
			});
		}
	}
	return map;
})();

/**
 * Resolve a SKU string into the canonical (productType, wood?, metal) tuple.
 * Returns `undefined` if the SKU is unknown, empty, or malformed.
 *
 * Round-trip guarantee: for every `(productType, wood, metal)` in `skuTable`,
 * `lookupSkuCombo(skuTable[skuKey(productType, wood, metal)])` returns
 * exactly that tuple.
 */
export function lookupSkuCombo(sku: string): SkuCombo | undefined {
	if (!sku) {
		return undefined;
	}
	return comboFromSku.get(sku);
}
